import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormatDateTimeDDMMYYHHMMSS } from "../../Logic/DateTimeFormatters";
import {
    Departments,
    GetMailboxRoles,
    MailboxList,
    MailboxRolesListItemDTO,
} from "../../Logic/EmsAPI/mailboxAccessRequest";
import { RootState } from "../../state/store";
import Confirm from "../Generic/ConfirmDialog";
import DropdownWithQuickFind from "../Generic/Form/DropDownWithQuickFind";
import MailboxAccessRequestDetailsModal from "./MailboxAccessRequestDetailsModal";
import "./styles.scss";
import {
    CreateMailboxAccessRequest,
    getTenantDepartments,
    getTenantMailboxes,
    MailboxAccessRequest,
} from "./tenantApiCall";
import { useMailboxAccessRequestsList } from "./useMailboxAccessRequestsList";

export interface MailboxAccressRequestsProps {
    isManager?: boolean;
    onBack?: () => void;
}

const MailboxRoleTitles: Record<string, string> = {
    "Level 4": "Users managing people, workflows, or escalations",
    "Level 3": "Senior staff handling escalations or complex cases",
    "Level 2": "Frontline operational staff",
    "Level 1": "Trainees or junior staff",
    View: "Oversight-only users",
    "View All": "Oversight-only users",
    "View / View All": "Oversight-only users",
};

function MailboxAccressRequests({ isManager = false, onBack }: MailboxAccressRequestsProps) {
    const UserInfo = useSelector((state: RootState) => state.UserInformation);
    const { isLoading, requests, loadData } = useMailboxAccessRequestsList(MailboxAccessRequest);
    const [mailboxes, setMailboxes] = useState<Array<MailboxList>>([]);
    const [mailboxRoles, setMailboxRoles] = useState<Array<MailboxRolesListItemDTO>>([]);
    const [departments, setDepartments] = useState<Array<Departments>>([]);
    const [showRequestModal, setShowRequestModal] = useState<boolean>(false);
    const [selectedRequest, setSelectedRequest] = useState<MailboxAccessRequest>();
    const [mailboxId, setMailboxId] = useState<number>(0);
    const [roleId, setRoleId] = useState<number>(0);
    const [motivationComment, setMotivationComment] = useState<string>("");

    useEffect(() => {
        (async () => {
            await loadData(items => {
                if (items.length === 0) setShowRequestModal(true);
            });
        })();
        (async () => {
            await loadFormData();
        })();
    }, []);

    async function loadFormData() {
        const mailboxes_results = await getTenantMailboxes();
        const mailbox_roles_results = await GetMailboxRoles();
        setMailboxes(mailboxes_results);
        if (mailbox_roles_results.isOk) {
            setMailboxRoles(mailbox_roles_results.data?.items!);
        }
        setDepartments(await getTenantDepartments());
    }

    const availableMailboxes = mailboxes.filter(
        mailbox =>
            mailbox.boxEnabled &&
            !UserInfo.mailboxes.some(userMailbox => userMailbox.id === mailbox.id) &&
            (!isManager || mailbox.managerID !== UserInfo.userID),
    );

    const availableMailboxRoles = mailboxRoles
        .filter(role => !role.name.toLowerCase().includes("admin"))
        .map(role => ({ ...role, title: MailboxRoleTitles[role.name] }));

    function closeRequestModal() {
        setShowRequestModal(false);
        setMailboxId(0);
        setRoleId(0);
        setMotivationComment("");
    }

    async function submitMailboxAccessRequest() {
        const result = await CreateMailboxAccessRequest({
            UserID: UserInfo.userID,
            MailboxID: mailboxId,
            RoleID: roleId,
            MotivationComment: motivationComment || null,
        });
        if (result.isOk) {
            closeRequestModal();
            await loadData();
        } else {
            alert("Failed to submit mailbox access request.");
        }
    }

    const isRequestValid = UserInfo.userID > 0 && mailboxId > 0 && roleId > 0;

    return (
        <>
            <div className="header">
                Mailbox Access Requests
                <div style={{ textAlign: "right", float: "right", marginRight: "10px" }}>
                    {isManager && (
                        <button
                            className="g-btn-default"
                            style={{ fontSize: "9pt", textAlign: "right", marginRight: "10px" }}
                            onClick={onBack}
                        >
                            {" "}
                            Back
                        </button>
                    )}
                    <button
                        className="g-btn-action"
                        style={{ fontSize: "9pt", textAlign: "right" }}
                        onClick={() => setShowRequestModal(true)}
                    >
                        {" "}
                        Request Access
                    </button>
                </div>
            </div>
            <div style={{ padding: "20px", paddingTop: "13px" }}>
                {isLoading && <div>Loading...</div>}
                {!isLoading && (
                    <table className="roles-table">
                        <thead>
                            <tr>
                                <th>Requested Date</th>
                                <th>Mailbox</th>
                                <th>Mailbox Manager</th>
                                <th>Requested Role</th>
                                <th>Status</th>
                                <th>Updated Date</th>
                                <th>Updated By</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length === 0 && (
                                <tr>
                                    <td colSpan={8}>No mailbox access requests found.</td>
                                </tr>
                            )}
                            {requests.map(request => (
                                <tr
                                    key={request.userMailboxAccessRequestID}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setSelectedRequest(request)}
                                >
                                    <td>
                                        {request.dateCreated ? FormatDateTimeDDMMYYHHMMSS(request.dateCreated) : ""}
                                    </td>
                                    <td>{request.mailboxName}</td>
                                    <td>{request.mailboxManagerName}</td>
                                    <td>{request.roleName}</td>
                                    <td>{request.accessRequestStatusName}</td>
                                    <td>
                                        {request.updatedAt ? FormatDateTimeDDMMYYHHMMSS(request.updatedAt) : ""}
                                    </td>
                                    <td>
                                        {request.updateUser
                                            ? `${request.updateUser.firstname ?? ""} ${
                                                  request.updateUser.lastname ?? ""
                                              }`.trim()
                                            : ""}
                                    </td>
                                    <td></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {showRequestModal && (
                <Confirm
                    title="Request Mailbox Access"
                    onConfirm={submitMailboxAccessRequest}
                    onCancel={closeRequestModal}
                    isValid={isRequestValid}
                >
                    <div style={{ width: "400px" }}>
                        <DropdownWithQuickFind
                            id="mailboxId"
                            label="Mailbox"
                            value={mailboxId === 0 ? "" : mailboxId.toString()}
                            returnFunc={newValue => setMailboxId(Number(newValue))}
                            Valid={true}
                            options={availableMailboxes.map(item => ({ text: item.name, value: item.id.toString() }))}
                        ></DropdownWithQuickFind>
                        <DropdownWithQuickFind
                            id="roleId"
                            label="Role"
                            value={roleId === 0 ? "" : roleId.toString()}
                            returnFunc={newValue => setRoleId(Number(newValue))}
                            Valid={true}
                            options={availableMailboxRoles.map(item => ({
                                text: item.name,
                                value: item.id.toString(),
                                title: item.title,
                            }))}
                        ></DropdownWithQuickFind>
                        <div className="row rowMarginControl">
                            <div className="col-sm-3">
                                <label className="g-label">Motivation</label>
                            </div>
                            <div className="col-sm-9">
                                <textarea
                                    className="g-TextArea-default"
                                    value={motivationComment}
                                    onChange={e => setMotivationComment(e.currentTarget.value)}
                                    maxLength={512}
                                    style={{ minWidth: "294px" }}
                                />
                            </div>
                        </div>
                    </div>
                </Confirm>
            )}
            {selectedRequest && (
                <MailboxAccessRequestDetailsModal
                    request={selectedRequest}
                    departments={departments}
                    onClose={() => setSelectedRequest(undefined)}
                />
            )}
        </>
    );
}
export default MailboxAccressRequests;
